/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {cloneDeep, isPlainObject, merge} from 'lodash';

import breakpointsTheme from '@amblique/base/app/theme/foundations/breakpoints';

/**
 * Responsive Helper
 *
 * Pure JavaScript logic for responsive options, srcset parsing, and media queries.
 * Object-based pattern allowing method overriding in downstream packages.
 *
 * Handles:
 * - Responsive options (breakpoint-based option switching)
 * - Srcset parsing (device-specific asset URLs)
 * - Media query utilities
 */
export const responsiveHelper = {
  // Performance cache for parseValue
  _cache: {
    breakpoints: null, // Sorted valid breakpoint names
    reversedBreakpoints: null, // Reversed breakpoint order (desktop-first)
    devices: null, // Valid device names
    deviceToBreakpointMap: null,
    breakpointToDeviceMap: null,
  },

  /**
   * Get sorted breakpoint names (cached)
   * @returns {Array<string>} Breakpoint names sorted by numeric value
   */
  get breakpoints() {
    if (!this._cache.breakpoints) {
      const {breakpoints} = breakpointsTheme;

      // Sort by numeric value (assumes consistent unit across all breakpoints)
      this._cache.breakpoints = Object.keys(breakpoints).sort((a, b) => {
        const aValue = parseFloat(breakpoints[a]);
        const bValue = parseFloat(breakpoints[b]);

        return aValue - bValue;
      });
    }

    return this._cache.breakpoints;
  },

  /**
   * Get reversed breakpoint order (cached)
   * Used for desktop-first cascading
   * @returns {Array<string>} Breakpoint names in reverse order (desktop-first)
   */
  get reversedBreakpoints() {
    if (!this._cache.reversedBreakpoints) {
      this._cache.reversedBreakpoints = [...this.breakpoints].reverse();
    }

    return this._cache.reversedBreakpoints;
  },

  /**
   * Get breakpoint value with original unit (em, rem, px, etc.)
   * Preserves the unit from theme for use in media queries
   * @param {string} breakpoint - Breakpoint name
   * @returns {string|null} Breakpoint value with unit (e.g., "48em") or null if not found
   */
  getBreakpointValue(breakpoint) {
    const {breakpoints} = breakpointsTheme;

    return breakpoints[breakpoint] || null;
  },

  /**
   * Get valid device names (cached)
   * @returns {Array<string>} Valid device names
   */
  get devices() {
    if (!this._cache.devices) {
      const {devices} = breakpointsTheme;

      this._cache.devices = Object.keys(devices);
    }

    return this._cache.devices;
  },

  /**
   * Get device to breakpoint mapping (cached)
   * @returns {Object<string, string>}
   */
  get deviceToBreakpointMap() {
    if (!this._cache.deviceToBreakpointMap) {
      const {devices} = breakpointsTheme;

      this._cache.deviceToBreakpointMap = {
        mobile: devices.mobile.enter,
        tablet: devices.tablet.enter,
        desktop: devices.desktop.enter,
      };
    }

    return this._cache.deviceToBreakpointMap;
  },

  /**
   * Get breakpoint to device mapping (cached)
   * @returns {Object<string, string>}
   */
  get breakpointToDeviceMap() {
    if (!this._cache.breakpointToDeviceMap) {
      const {devices} = breakpointsTheme;
      const map = {};

      // Get breakpoint order (uses getter)
      const breakpointOrder = this.breakpoints;

      // Build map based on enter/leave ranges
      for (const [deviceName, {enter, leave}] of Object.entries(devices)) {
        const enterIndex = breakpointOrder.indexOf(enter);
        const leaveIndex = leave ? breakpointOrder.indexOf(leave) : breakpointOrder.length;

        for (let i = enterIndex; i < leaveIndex; i++) {
          map[breakpointOrder[i]] = deviceName;
        }
      }

      this._cache.breakpointToDeviceMap = map;
    }

    return this._cache.breakpointToDeviceMap;
  },

  /**
   * Get matched value from media queries
   *
   * @param {MediaQueryList[]} mediaQueries - Array of MediaQueryList objects
   * @returns {any} Matched value (last match wins - desktop-first)
   */
  getMatchedMediaValue(mediaQueries) {
    let matchedValue = null;

    for (const mediaQuery of mediaQueries) {
      if (mediaQuery.matches) {
        if (isPlainObject(mediaQuery.value) && isPlainObject(matchedValue)) {
          // Deep merge without mutating - create new object each time
          matchedValue = merge(cloneDeep(matchedValue), mediaQuery.value);
        } else {
          matchedValue = cloneDeep(mediaQuery.value);
        }
      }
    }

    return matchedValue;
  },

  /**
   * Parse and normalize responsive options
   *
   * Converts various responsive formats to standardized media queries:
   *
   * Input formats:
   * 1. Named breakpoints: { mobile: {...}, tablet: {...}, desktop: {...} }
   * 2. Shorthand: { 'max-768': {...}, 'min-1200': {...} }
   * 3. Media queries: { '(max-width: 768px)': {...} }
   *
   * @param {object} responsive - Responsive options object
   * @returns {object} Normalized media query map
   */
  parseResponsiveOptions(responsive = {}) {
    const normalized = {};

    for (const [key, value] of Object.entries(responsive)) {
      // Named breakpoint: 'mobile', 'tablet', 'sm', 'md'
      const breakpointValue = this.getBreakpointValue(key);

      if (breakpointValue) {
        // Use original unit from theme
        const query = `(min-width: ${breakpointValue})`;

        normalized[query] = value;
        continue;
      }

      // Media query format: '(max-width: 768px)'
      if (key.startsWith('(')) {
        normalized[key] = value;
        continue;
      }

      // Shorthand format: 'max-768', 'min-1200'
      const match = key.match(/^(min|max)-(\d+)$/);

      if (match) {
        const query = `(${match[1]}-width: ${match[2]}px)`;

        normalized[query] = value;
      }
    }

    return this.sortMediaQueries(normalized);
  },

  /**
   * Sort media queries (mobile-first approach)
   *
   * @param {object} mediaQueries - Media query map
   * @returns {object} Sorted media query map
   */
  sortMediaQueries(mediaQueries) {
    const sorted = Object.keys(mediaQueries).sort((a, b) => {
      const aMatch = a.match(/(\d+)/);
      const bMatch = b.match(/(\d+)/);

      // Handle queries without numbers (like "default") - stable sort
      if (!aMatch && !bMatch) return 0;
      if (!aMatch) return -1; // Non-numeric queries come first
      if (!bMatch) return 1;

      const aValue = parseInt(aMatch[1]);
      const bValue = parseInt(bMatch[1]);

      // min-width: smaller values first (mobile-first)
      if (a.includes('min-width') && b.includes('min-width')) {
        return aValue - bValue;
      }

      // max-width: larger values first
      if (a.includes('max-width') && b.includes('max-width')) {
        return bValue - aValue;
      }

      // min-width before max-width
      if (a.includes('min-width')) return -1;
      if (b.includes('min-width')) return 1;

      return 0;
    });

    return sorted.reduce((result, query) => {
      result[query] = mediaQueries[query];

      return result;
    }, {});
  },

  /**
   * Get matched responsive options for current viewport
   *
   * @param {object} responsiveOptions - Parsed responsive options
   * @param {number} currentWidth - Current viewport width
   * @returns {object} Merged options for current viewport
   */
  getMatchedResponsiveOptions(responsiveOptions, currentWidth) {
    let matched = {};

    for (const [query, options] of Object.entries(responsiveOptions)) {
      if (this.matchesMediaQuery(query, currentWidth)) {
        // Deep merge without mutating - create new object each time
        matched = merge(cloneDeep(matched), options);
      }
    }

    return matched;
  },

  /**
   * Check if media query matches current width
   *
   * @param {string} query - Media query string
   * @param {number} currentWidth - Current viewport width
   * @returns {boolean} True if matches
   */
  matchesMediaQuery(query, currentWidth) {
    const match = query.match(/\((min|max)-width:\s*(\d+)px\)/);

    if (!match) return false;

    const type = match[1];
    const width = parseInt(match[2]);

    if (type === 'min') {
      return currentWidth >= width;
    } else {
      return currentWidth <= width;
    }
  },

  /**
   * Create MediaQueryList objects from parsed media queries
   *
   * @param {object} mediaQueries - Media query map
   * @param {Function} listener - Change listener function
   * @returns {Array} Array of MediaQueryList wrapper objects
   */
  createMediaQueryLists(mediaQueries, listener) {
    const lists = [];

    for (const [query, value] of Object.entries(mediaQueries)) {
      const mql = window.matchMedia(query);

      // Wrap in custom object instead of polluting native MediaQueryList
      const wrapper = {
        mql,
        value,
        listener,
        matches: mql.matches,
      };

      lists.push(wrapper);

      // Register listener
      if (listener) {
        if (mql.addEventListener) {
          mql.addEventListener('change', listener, {passive: true});
        } else {
          mql.addListener(listener);
        }
      }
    }

    return lists;
  },

  /**
   * Cleanup MediaQueryList listeners
   *
   * @param {Array} mediaQueryLists - Array of MediaQueryList wrapper objects
   */
  cleanupMediaQueryLists(mediaQueryLists) {
    mediaQueryLists.forEach((wrapper) => {
      const {mql, listener} = wrapper;

      if (listener) {
        if (mql.removeEventListener) {
          mql.removeEventListener('change', listener);
        } else {
          mql.removeListener(listener);
        }
      }
    });
  },

  /**
   * Parse responsive value string into structured object
   *
   * Supports CSS property-like syntax for defining responsive values:
   * - Named breakpoints: "mobile: 320, tablet: 640, desktop: 1280"
   * - Shorthand: "320, 640, 1280" (mobile, tablet, desktop)
   * - Default modifier: "mobile: 320, tablet: 640 !default"
   * - Mixed separators: comma, semicolon, newline
   *
   * @param {string} input - Responsive value string to parse
   * @param {object} options - Optional configuration
   * @param {string} options.type - Value type: 'string', 'integer', 'number', 'boolean' (default: 'string')
   * @param {string} options.format - Output format: 'device', 'breakpoint' (default: 'device')
   * @returns {object} Parsed responsive value object
   *
   * @example
   * // Device format (default)
   * parseValue('mobile: 320, tablet: 640, desktop: 1280')
   * // => { isResponsive: true, isEmpty: false, breakpoints: ['mobile', 'tablet', 'desktop'],
   * //      value: null, mobile: '320', tablet: '640', desktop: '1280' }
   *
   * @example
   * // Breakpoint format (for Chakra UI)
   * parseValue('mobile: 320, tablet: 640, desktop: 1280', {format: 'breakpoint'})
   * // => { base: '320', md: '640', xl: '1280' }
   *
   * @example
   * // Shorthand syntax
   * parseValue('320, 640, 1280')
   * // => { value: '320', mobile: '320', tablet: '640', desktop: '1280', ... }
   *
   * @example
   * // Type coercion
   * parseValue('mobile: 320, tablet: abc, desktop: 1280', {type: 'integer'})
   * // => { mobile: 320, desktop: 1280 } // tablet omitted (invalid)
   */
  parseValue(input, options = {}) {
    const {type = 'string', format = 'device'} = options;

    // Handle null/undefined/empty input
    if (!input || typeof input !== 'string') {
      return format === 'device' ?
        {isResponsive: false, isEmpty: true, breakpoints: [], value: null} :
        {};
    }

    // Parse entries
    const entries = this._parseResponsiveEntries(input);

    if (entries.length === 0) {
      return format === 'device' ?
        {isResponsive: false, isEmpty: true, breakpoints: [], value: null} :
        {};
    }

    // Separate descriptors and non-descriptors
    const withDescriptors = entries.filter((e) => e.descriptor !== null);
    const withoutDescriptors = entries.filter((e) => e.descriptor === null);

    // Build raw result
    let rawResult = {};
    let defaultValue = null;
    let defaultMarkedBy = null; // Track what marked the default

    // Process entries with descriptors
    for (const {value, descriptor, isDefault} of withDescriptors) {
      // Track if this is marked as !default
      if (isDefault) {
        defaultMarkedBy = {type: 'modifier', value, descriptor};
      }

      // Convert descriptor to target format
      const targetKey = this._convertDescriptor(descriptor, format);

      if (targetKey !== null) {
        rawResult[targetKey] = value;
      }
    }

    // Process entries without descriptors (shorthand)
    if (withoutDescriptors.length > 0) {
      const shorthandValues = withoutDescriptors.map((e) => e.value);

      // Apply shorthand rules
      const shorthandResult = this._applyShorthandRules(shorthandValues, format);

      // Merge shorthand into result (shorthand doesn't override explicit descriptors)
      rawResult = {...shorthandResult, ...rawResult};

      // Default from shorthand (first value, unless modifier overwrites)
      if (!defaultMarkedBy) {
        defaultMarkedBy = {type: 'shorthand', value: shorthandValues[0]};
      }
    }

    // Apply type coercion
    const coercedResult = this._applyTypeCoercion(rawResult, type);

    // Determine default value
    if (defaultMarkedBy) {
      if (defaultMarkedBy.type === 'modifier') {
        // Find the coerced value for the descriptor that was marked
        const targetKey = this._convertDescriptor(defaultMarkedBy.descriptor, format);

        defaultValue = coercedResult[targetKey] ?? null;
      } else {
        // Shorthand default (first value, coerced)
        const coerced = this._coerceValue(defaultMarkedBy.value, type);

        defaultValue = coerced.valid ? coerced.value : null;
      }
    }

    // Return based on format
    if (format === 'breakpoint') {
      // Breakpoint format: minimal object, no metadata
      return coercedResult;
    }

    // Device format: full metadata
    const deviceKeys = Object.keys(coercedResult).filter((k) => this.devices.includes(k));
    const isResponsive = deviceKeys.length >= 2;
    const isEmpty = deviceKeys.length === 0;

    return {
      isResponsive,
      isEmpty,
      breakpoints: deviceKeys,
      value: defaultValue,
      ...coercedResult,
    };
  },

  /**
   * Parse responsive value string into entries
   * @private
   * @param {string} input - Input string
   * @returns {Array<{value: string, descriptor: string|null, isDefault: boolean}>}
   */
  _parseResponsiveEntries(input) {
    // Split by separators: comma, semicolon, newline
    const rawEntries = input
      .split(/[,;\n]/)
      .map((entry) => entry.trim())
      .filter(Boolean);

    const entries = [];

    for (const entry of rawEntries) {
      // Check for descriptor format: "descriptor: value" or "descriptor: value !default"
      const descriptorMatch = entry.match(/^([a-z0-9+\-()\s]+):\s*(.*)$/i);

      if (descriptorMatch) {
        const descriptor = descriptorMatch[1].trim().toLowerCase();
        let value = descriptorMatch[2].trim();
        let isDefault = false;

        // Check for !default modifier
        if (value.endsWith('!default')) {
          isDefault = true;
          value = value.slice(0, -8).trim(); // Remove '!default'
        }

        entries.push({value, descriptor, isDefault});
      } else {
        // No descriptor (shorthand format)
        let value = entry.trim();
        let isDefault = false;

        // Check for !default modifier
        if (value.endsWith('!default')) {
          isDefault = true;
          value = value.slice(0, -8).trim();
        }

        entries.push({value, descriptor: null, isDefault});
      }
    }

    return entries;
  },

  /**
   * Convert descriptor to target format
   * @private
   * @param {string} descriptor - Descriptor name (normalized to lowercase)
   * @param {string} format - Target format ('device' or 'breakpoint')
   * @returns {string|null} Converted descriptor or null if invalid
   */
  _convertDescriptor(descriptor, format) {
    // Check if it's a valid breakpoint
    const isBreakpoint = this.breakpoints.includes(descriptor);

    // Check if it's a valid device
    const isDevice = this.devices.includes(descriptor);

    if (!isBreakpoint && !isDevice) {
      return null; // Invalid descriptor
    }

    // Convert to target format
    if (format === 'device') {
      return isDevice ? descriptor : this.breakpointToDeviceMap[descriptor];
    } else {
      return isBreakpoint ? descriptor : this.deviceToBreakpointMap[descriptor];
    }
  },

  /**
   * Apply shorthand rules (1, 2, or 3 values)
   * @private
   * @param {Array<string>} values - Shorthand values
   * @param {string} format - Target format ('device' or 'breakpoint')
   * @returns {Object} Shorthand result object
   */
  _applyShorthandRules(values, format) {
    const result = {};

    // Take only first 3 values (ignore extras)
    const limited = values.slice(0, 3);

    if (format === 'device') {
      // Use actual device names from theme (support dynamic devices)
      const deviceNames = this.devices;

      if (limited.length === 1) {
        // 1 value: all devices
        deviceNames.forEach((device) => {
          result[device] = limited[0];
        });
      } else if (limited.length === 2) {
        // 2 values: first device(s), last device
        // Assumption: first for mobile+tablet, second for desktop
        if (deviceNames.length === 3) {
          result[deviceNames[0]] = limited[0]; // mobile
          result[deviceNames[1]] = limited[0]; // tablet
          result[deviceNames[2]] = limited[1]; // desktop
        } else {
          // Generic fallback for different device counts
          const midpoint = Math.floor(deviceNames.length / 2);

          deviceNames.forEach((device, index) => {
            result[device] = index < midpoint ? limited[0] : limited[1];
          });
        }
      } else if (limited.length === 3) {
        // 3 values: map to first 3 devices
        deviceNames.slice(0, 3).forEach((device, index) => {
          result[device] = limited[index];
        });
      }
    } else {
      // Breakpoint format
      // Get the breakpoints for shorthand mapping
      const deviceMap = this.deviceToBreakpointMap;
      const mobileBreakpoint = deviceMap.mobile || 'base';
      const tabletBreakpoint = deviceMap.tablet || 'md';
      const desktopBreakpoint = deviceMap.desktop || 'xl';

      if (limited.length === 1) {
        // 1 value: base only
        result[mobileBreakpoint] = limited[0];
      } else if (limited.length === 2) {
        // 2 values: base, desktop
        result[mobileBreakpoint] = limited[0];
        result[desktopBreakpoint] = limited[1];
      } else if (limited.length === 3) {
        // 3 values: mobile, tablet, desktop breakpoints
        result[mobileBreakpoint] = limited[0];
        result[tabletBreakpoint] = limited[1];
        result[desktopBreakpoint] = limited[2];
      }
    }

    return result;
  },

  /**
   * Apply type coercion to all values
   * @private
   * @param {Object} rawResult - Raw result object
   * @param {string} type - Value type
   * @returns {Object} Coerced result object
   */
  _applyTypeCoercion(rawResult, type) {
    const coerced = {};

    for (const [key, value] of Object.entries(rawResult)) {
      const result = this._coerceValue(value, type);

      // Only include valid values (omit invalid)
      if (result.valid) {
        coerced[key] = result.value;
      }
    }

    return coerced;
  },

  /**
   * Coerce a single value to target type
   * @private
   * @param {string} value - Value to coerce
   * @param {string} type - Target type
   * @returns {{valid: boolean, value: any}} Coercion result
   */
  _coerceValue(value, type) {
    // Empty string is valid for all types
    if (value === '') {
      return {valid: true, value: ''};
    }

    switch (type) {
      case 'integer': {
        const parsed = parseInt(value, 10);

        if (isNaN(parsed)) {
          return {valid: false, value: null};
        }

        return {valid: true, value: parsed};
      }

      case 'number': {
        const parsed = parseFloat(value);

        if (isNaN(parsed)) {
          return {valid: false, value: null};
        }

        return {valid: true, value: parsed};
      }

      case 'boolean': {
        const normalized = value.toString().toLowerCase().trim();
        const falsyValues = ['false', 'no', '0', 'off', 'disabled', ''];

        return {
          valid: true,
          value: !falsyValues.includes(normalized),
        };
      }

      case 'string':
      default:
        return {valid: true, value};
    }
  },
};
